import { useEffect, useMemo, useRef, useState } from 'react';

const WIDGET_SCRIPT_URL = (import.meta.env
  .VITE_REACT_APP_AMLBOT_WIDGET_URL as string)!;
const WIDGET_APP_ID = (import.meta.env
  .VITE_REACT_APP_AMLBOT_WIDGET_APP_ID as string)!;

const WIDGET_SETTINGS = {
  appId: WIDGET_APP_ID,
  placement: 'bottom-left',
  mode: 'inline',
  containerId: 'amlbot-inline-container',
};

type WidgetMethod = 'init' | 'open' | 'close' | 'toggle' | 'sendData';

interface AmlBotWidgetInstance {
  init: (params: { username: string; externalId: string }) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  sendData: (params: { hash: string; network: string }) => void;
  isReady: () => boolean;
  isLoading: boolean;
  loadFailed: boolean;
}

/**
 * Loads the AMLBot widget embed script and returns a typed instance
 * for calling widget methods (init, open, close, toggle, sendData).
 * Tracks widget readiness reactively via the WIDGET_READY postMessage event.
 * The widget is fully cleaned up on unmount.
 */
export const useAmlBotWidget = (): AmlBotWidgetInstance => {
  const isCleanedUp = useRef(false);
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    isCleanedUp.current = false;

    (window as any).amlBotSettings = WIDGET_SETTINGS;

    if (!(window as any).AMLBotWidget) {
      (window as any).AMLBotWidget = function (...args: any[]) {
        (window as any).AMLBotWidget.q.push(args);
      };
      (window as any).AMLBotWidget.q = [] as any[];
    }

    const existingScript = document.querySelector(
      `script[src="${WIDGET_SCRIPT_URL}"]`,
    );
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = WIDGET_SCRIPT_URL;
      script.onerror = () => {
        if (!isCleanedUp.current) {
          setLoadFailed(true);
        }
      };
      document.head.appendChild(script);
    }

    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'WIDGET_READY') {
        setReady(true);
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      isCleanedUp.current = true;
      setReady(false);
      setLoadFailed(false);

      window.removeEventListener('message', onMessage);

      if (typeof (window as any).AMLBotWidget === 'function') {
        (window as any).AMLBotWidget('close');
      }

      const scriptEl = document.querySelector(
        `script[src="${WIDGET_SCRIPT_URL}"]`,
      );
      if (scriptEl) {
        scriptEl.remove();
      }

      ['amlbot-wrapper', 'amlbot-toggle-button'].forEach((id) => {
        document.getElementById(id)?.remove();
      });

      delete (window as any).amlBotSettings;
      delete (window as any).AMLBotWidget;
    };
  }, []);

  const widget = useMemo<AmlBotWidgetInstance>(() => {
    const call = (method: WidgetMethod, params?: any): any => {
      if (isCleanedUp.current) return;
      return (window as any).AMLBotWidget?.(method, params);
    };

    return {
      init: (params) => call('init', params),
      open: () => call('open'),
      close: () => call('close'),
      toggle: () => call('toggle'),
      sendData: (params) => call('sendData', params),
      isReady: () => ready,
      isLoading: !ready && !loadFailed,
      loadFailed,
    };
  }, [ready, loadFailed]);

  return widget;
};
